<!--
  PLACEHOLDER — Income Estimate (demo)

  Spot price configuration modal. Visually complete; functionally the
  Apply button just closes the modal. When this becomes a real feature,
  the submit handler should trigger a recompute of the Spot stream values
  using the chosen method (weighted3 / weighted6 / lastSale / manual
  override), then refetch the chart + table data.

  Imperative API: parent calls `spotConfigModal.value?.show()` to open,
  matching the project's existing modal usage (see
  ProductionEstimateOverrideModal.vue).
-->
<template>
  <ModalComponent
    ref="modal"
    submit-button-text="Apply assumptions"
    :submit="handleApply"
  >
    <template #title>Spot price assumptions</template>
    <template #body>
      <p class="text-muted small mb-3">
        How should we project the price you'll get for residual GOs sold on the
        spot market?
      </p>
      <div class="mb-3">
        <div class="form-label fw-semibold small text-uppercase text-muted">
          Pricing method
        </div>
        <div class="d-flex flex-column gap-2">
          <div
            v-for="m in methods"
            :key="m.key"
            :class="['method-row', { 'is-on': model.method === m.key }]"
            @click="selectMethod(m.key)"
          >
            <span class="radio" />
            <div>
              <div class="fw-medium">{{ m.label }}</div>
              <div class="small text-muted">{{ m.sub }}</div>
            </div>
            <div class="fw-semibold font-variant-numeric-tabular">
              €{{ priceFor(m.key).toFixed(2) }}
            </div>
          </div>
        </div>
      </div>
      <div>
        <div class="form-label fw-semibold small text-uppercase text-muted">
          Applied price
        </div>
        <div class="border rounded p-3">
          <div class="fw-semibold mb-1">Any renewable</div>
          <div class="fs-6 fw-medium font-variant-numeric-tabular">
            €{{ priceFor(model.method).toFixed(2)
            }}<span class="small text-muted fw-normal"> / MWh</span>
          </div>
        </div>
      </div>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import { useTemplateRef } from "vue";
import type { SpotPrices } from "./incomeEstimateDemoData";

type MethodKey = SpotPrices["method"];

const modal = useTemplateRef<InstanceType<typeof ModalComponent>>("modal");
const model = defineModel<SpotPrices>("prices", { required: true });

const methods: { key: MethodKey; label: string; sub: string }[] = [
  {
    key: "weighted3",
    label: "Weighted avg of last 3 sales",
    sub: "Recommended · adapts quickly to market shifts",
  },
  {
    key: "weighted6",
    label: "Weighted avg of last 6 sales",
    sub: "Smoother · less sensitive to single-trade outliers",
  },
  {
    key: "lastSale",
    label: "Most recent sale price",
    sub: "Aggressive · reflects latest market signal",
  },
  {
    key: "override",
    label: "Manual override",
    sub: "Set your own assumption per technology",
  },
];

const priceFor = (m: MethodKey): number => {
  const r = model.value.renewable;
  if (m === "override") return r.override ?? r.weighted3;
  return r[m];
};

const selectMethod = (key: MethodKey) => {
  model.value = { ...model.value, method: key };
};

const handleApply = () => {
  // PLACEHOLDER — real implementation should re-fetch streams with the
  // chosen method before closing. For now we just close.
  modal.value?.toggleModal();
};

const show = () => modal.value?.toggleModal();

defineExpose({ show });
</script>

<style scoped lang="scss">
/* Only the radio-card method picker needs custom CSS — Bootstrap has no
   matching primitive. Everything else uses Bootstrap utilities + ModalComponent. */
.method-row {
  display: grid;
  grid-template-columns: 18px 1fr auto;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid var(--phoenix-gray-200);
  border-radius: var(--phoenix-border-radius);
  cursor: pointer;
  background: var(--phoenix-white);

  &.is-on {
    border-color: var(--phoenix-primary);
    background: rgba(var(--phoenix-primary-rgb), 0.03);
  }

  .radio {
    width: 14px;
    height: 14px;
    border: 1.4px solid var(--phoenix-gray-400);
    border-radius: 50%;
    display: grid;
    place-items: center;
  }

  &.is-on .radio {
    border-color: var(--phoenix-primary);

    &::after {
      content: "";
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: var(--phoenix-primary);
    }
  }
}

.font-variant-numeric-tabular {
  font-variant-numeric: tabular-nums;
}
</style>
